<template lang="pug">
    v-dialog(v-model="dialog" max-width="450" scrollable persistent :fullscreen="$vuetify.breakpoint.smAndDown")
        v-card(tile)
            v-card-title.title.caption--text {{title}}
            v-container(tag="v-card-text" gris-list-xl mb-0)
                v-overlay(:value="loading" absolute)
                    v-progress-circular(indeterminate size="64")
                v-layout(wrap)
                    v-flex(xs12)
                        v-alert(color="header" colored-border elevation="2" border="left" icon="mdi-forum-outline")
                            v-breadcrumbs(:items="breadcrumbs").pa-0
                                template(#divider)
                                    v-icon mdi-forward
                                template(#item="props")
                                    v-breadcrumbs-item(:class="[props.item.disabled && 'disabled']")
                                        | {{ props.item.text }}
                    v-flex(xs12)
                        v-text-field(v-model="search" clearable ref="refSearch"
                            :loading="typing || searching"
                            type="search"
                            @input="typing = true"
                            solo
                            :hide-details="true"
                            persistent-hint
                            prepend-inner-icon="mdi-magnify")

                    v-flex(xs12 v-if="roomsFiltered.length")
                        v-list.mt-2
                            template(v-for="(room, index) in roomsFiltered")
                                v-divider(v-if="index === 0")
                                v-list-item(:key="room.id"
                                    @click="onSelectRoom(room)")
                                    v-list-item-content.subtitle-2
                                        highlighter(:text="room.name" :keyword="search")
                                v-divider
                    v-flex(xs12 layout column my-3 mx-auto justify-center align-center v-if="showNewButton")
                        .subtitle-2.my-2 {{$t("pages.chat.finder.notFound")}}
                        v-btn(rounded color="primary" @click="onNewRoom" :loading="typing || searching")
                            v-icon(left) mdi-plus-outline
                            span {{$t("pages.chat.finder.create")}}
            v-card-actions
                v-spacer
                v-btn(@click="close" color="grey" depressed).px-3.white--text
                    v-icon(left dark) mdi-close-circle-outline
                    | {{$t('general.close')}}
</template>

<script>
import rules from "../../mixins/rules";
import general from "../../mixins/general";
import crud from "../../mixins/crud";
import _ from "lodash";

export default {
  name: "ChatRoomFinder",
  mixins: [rules, general, crud],
  data() {
    return {
      dialog: false,
      search: null,
      typing: false,
      searching: false,
      roomsSearched: [],
      subChannelId: null,
      breadcrumbs: [],
      loading: false,
      subChannel: null,
      cancelFunc: null
    };
  },
  computed: {
    showing: {
      get() {
        return this.dialog;
      }
    },
    title: {
      get() {
        return this.$t(`pages.chat.finder.title`);
      }
    },
    roomsFiltered: {
      get() {
        return typeof this.search !== "string" || !this.search.trim()
          ? this.roomsSearched
          : this.roomsSearched.filter(r =>
              r.name.toLowerCase().includes(this.search.toLowerCase())
            );
      }
    },
    showNewButton() {
      return (
        typeof this.search === "string" &&
        this.search.trim() &&
        !this.roomsFiltered.some(
          r => r.name.toLowerCase() === this.search.toLowerCase()
        )
      );
    }
  },
  watch: {
    search: _.debounce(function() {
      this.typing = false;
    }, 500),

    typing(value, before) {
      if (before && !value) {
        this.doSearchChatRoom();
      }
    }
  },
  methods: {
    clear() {
      this.subChannelId = null;
      this.search = null;
      this.breadcrumbs = [];
      this.subChannel = null;
    },
    show(subChannelId) {
      this.roomsSearched = [];
      this.subChannelId = subChannelId;
      this.dialog = true;
      this.loading = true;
      this.startSearch(false)
        .then(data => {
          const channelTitle = data.channel.title;
          const subChannelTitle = data.title;
          this.breadcrumbs.push({ text: channelTitle, disabled: true });
          this.breadcrumbs.push({ text: subChannelTitle, disabled: true });
          setTimeout(() => {
            this.$refs.refSearch.focus();
          }, 100);
          this.doSearchChatRoom();
        })
        .catch(err => {
          this.close();
          this.$nextTick(() => {
            this.$toasted.error(
              _.get(err, "response.message", this.$t("general.somethingWrong"))
            );
            this.$emit("wrong");
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    cancelSearchRequest() {
      if (this.searching && typeof this.cancelFunc === "function") {
        this.cancelFunc("Searching cancelled by user");
        this.searching = false;
      }
    },
    doSearchChatRoom() {
      if (!this.dialog || !this.subChannelId) {
        this.roomsSearched = [];
        return;
      }
      this.searching = true;
      this.startSearch(true)
        .then(({ rooms }) => {
          this.roomsSearched = rooms;
        })
        .finally(() => {
          this.searching = false;
        });
    },
    startSearch(withChatRoom = false) {
      let self = this;
      this.cancelSearchRequest();
      return new Promise((resolve, reject) => {
        this.axios
          .get(
            `sub-channels/${this.subChannelId}${
              withChatRoom ? "/chat-rooms" : ""
            }`,
            {
              cancelToken: new this.axios.CancelToken(function executor(c) {
                self.cancelFunc = c;
              })
            }
          )
          .then(({ data }) => {
            this.subChannel = { ...data.data };
            resolve(data.data);
          })
          .catch(err => {
            reject(err);
          });
      });
    },
    close() {
      this.cancelSearchRequest();
      this.dialog = false;
      this.clear();
    },
    onNewRoom() {
      this.loading = true;
      this.$store
        .dispatch("auth/fetchUser")
        .then(data => {
          if (data.perm_create_chat_room) {
            this.$emit("new", {
              subChannel: { ...this.subChannel },
              name: this.search.trim()
            });
            this.close();
          } else {
            this.$emit("wrong", {
              message: this.$t("general.permissionFailed.createChatRoom")
            });
            this.close();
          }
        })
        .catch(error => {
          const message = _.get(
            error,
            "response.data.message",
            this.$t("general.somethingWrong")
          );
          this.$toasted.error(message);
        })
        .finally(() => (this.loading = false));
    },
    onSelectRoom(room) {
      this.$emit("select", { ...room });
      this.close();
    }
  }
};
</script>
